import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Pencil, Trash2, X, Building2, Users2, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const empty = { nama: "", deskripsi: "" };

export default function Divisions() {
  const { user, can } = useAuth();
  // Halaman ini menerima DUA kunci izin, sama seperti backend
  // (`hr_config.py::_boleh_kelola_divisi`): `divisions` adalah kunci khususnya,
  // sementara gerbang menu "/divisions" di Layout.jsx memakai `master`. Kalau
  // di sini cuma salah satu yang dicek, selalu ada kelompok yang melihat menu
  // tapi tidak melihat tombolnya, atau sebaliknya.
  const bolehKelola = (aksi) => can("divisions", aksi) || can("master", aksi);
  const canCreate = bolehKelola("create");
  const canUpdate = bolehKelola("update");
  // DELETE /divisions/{id} SENGAJA masih superadmin saja — menghapus divisi ikut
  // mengosongkan field `divisi` semua karyawan yang terikat. Tombolnya dulu
  // tampil untuk admin dan selalu berakhir 403.
  const canDelete = user?.role === "superadmin";
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  // Kegagalan dua panggilan ini dulu tidak pernah dilaporkan: halaman tampil
  // "Belum ada divisi" padahal permintaannya ditolak/gagal, dan yang pertama
  // bahkan tanpa .catch sama sekali (unhandled rejection, tanpa pesan apa pun).
  const load = () => api.get("/divisions")
    .then(r => setItems(r.data || []))
    .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat daftar divisi."));
  useEffect(() => {
    load();
    api.get("/employees")
      .then(r => setEmps(r.data || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat data karyawan (jumlah anggota divisi tidak akan tampil)."));
  }, []);

  const openNew = () => { setForm(empty); setModal({ mode: "new" }); };
  const openEdit = (d) => { setForm({ nama: d.nama || "", deskripsi: d.deskripsi || "" }); setModal({ mode: "edit", id: d.id }); };

  const save = async () => {
    if (!form.nama.trim()) { toast.error("Nama divisi wajib"); return; }
    try {
      if (modal.mode === "edit") {
        await api.patch(`/divisions/${modal.id}`, form);
        toast.success("Divisi diperbarui");
      } else {
        await api.post("/divisions", form);
        toast.success("Divisi dibuat");
      }
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => {
    if (!confirm("Hapus divisi ini? Karyawan yang terikat akan otomatis dilepas.")) return;
    try { await api.delete(`/divisions/${id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const filtered = items.filter(d => !q || d.nama.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-4 md:p-8 max-w-[1200px]" data-testid="divisions-page">
      <header className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Master Data</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Master Divisi</h1>
          <p className="mt-2 text-sm text-slate-500">Kelola kategori divisi. Divisi dipakai untuk menentukan penempatan karyawan.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari divisi..."
              className="h-10 pl-8 pr-3 rounded-lg border border-slate-200 text-sm w-56" data-testid="input-search-divisi" />
          </div>
          {canCreate && (
            <button onClick={openNew} data-testid="btn-add-divisi"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
              <Plus className="w-4 h-4" /> Tambah Divisi
            </button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(d => {
          const memberCount = emps.filter(e => e.divisi === d.nama).length;
          return (
            <div key={d.id} data-testid={`divisi-card-${d.id}`}
              className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-emerald-400 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Building2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <h3 className="font-display font-semibold text-slate-900 truncate">{d.nama}</h3>
                </div>
                {(canUpdate || canDelete) && (
                  <div className="flex gap-1">
                    {canUpdate && (
                    <button onClick={() => openEdit(d)} data-testid={`btn-edit-divisi-${d.id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"><Pencil className="w-3.5 h-3.5" /></button>
                    )}
                    {canDelete && (
                    <button onClick={() => del(d.id)} data-testid={`btn-del-divisi-${d.id}`}
                      className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                )}
              </div>
              {d.deskripsi && <p className="text-xs text-slate-600 italic mt-2 line-clamp-2">{d.deskripsi}</p>}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                  <Users2 className="w-3 h-3" /> <b className="text-slate-800">{memberCount}</b> karyawan
                </p>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 bg-white border border-slate-200 rounded-xl p-16 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="font-display font-semibold text-slate-900">Belum ada divisi</p>
            {canCreate && (
              <button onClick={openNew} data-testid="btn-add-first-divisi"
                className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                <Plus className="w-4 h-4" /> Buat Divisi Pertama
              </button>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="divisi-modal">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">{modal.mode === "edit" ? "Edit Divisi" : "Divisi Baru"}</h3>
              <button onClick={() => setModal(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Nama Divisi *</span>
                <input value={form.nama} onChange={e => setForm(s => ({ ...s, nama: e.target.value }))} required autoFocus
                  data-testid="input-divisi-nama"
                  placeholder="cth: HR, Operational, Marketing, Finance"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Deskripsi (opsional)</span>
                <textarea value={form.deskripsi} onChange={e => setForm(s => ({ ...s, deskripsi: e.target.value }))}
                  rows={3} data-testid="input-divisi-deskripsi"
                  placeholder="Deskripsi singkat fungsi divisi ini..."
                  className="mt-1 w-full p-3 rounded-lg border border-slate-200 text-sm resize-none" />
              </label>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => setModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-divisi"
                  className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                  {modal.mode === "edit" ? "Perbarui" : "Simpan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
